import React, { useEffect } from "react";
import { useRef } from "react";
import { Col, Nav, Row, Tab, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

// Components
import Login from "../components/auth/Login";
import {
  LinkToApp,
  LinkToCommon,
  LinkToMarketing,
} from "../components/user-menu/Interlinks";

// Functions
import { themeColors } from "../helpers/themeColors";
import { isIframed } from "../helpers/utils";
import { useAutoIframeHeight } from "../hooks/utility";
import * as authActions from "../store/auth/actions";

const colors = themeColors();
const MARKETINGSITE = process.env.REACT_APP_MARKETING_SITE_URL;

const TabStyled = styled.div`
  .header-wrapper {
    background: var(--nav-gray);
    margin: auto;
  }
  .navbar-nav {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .nav-link {
    background: white !important;
    color: ${colors.darkBlue} !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: 0.15px;
    &:before {
      content: "";
      width: 100%;
      height: 4px;
      background: var(--primary);
      position: absolute;
      left: 0;
      z-index: 2;
      top: 0px;
    }
  }
  .sign-up {
    color: var(--black);
    font-size: 16px;
    font-weight: 400;
    line-height: 28px;
  }
  .navbar-nav .nav-link.active {
    background: white !important;
    color: var(--primary);
    font-size: 16px;
    font-weight: 700;
    line-height: 28px;
    letter-spacing: 0.15px;

    &:before {
      content: "";
      width: 100%;
      height: 4px;
      background: var(--primary);
      position: absolute;
      left: 0;
      z-index: 2;
      top: 0px;
    }

    &:hover {
      color: black;
    }
  }
  .tab-btn {
    padding-top: 10px;
    padding-bottom: 17px;
    display: flex;
    justify-content: center;
  }
`;

const UserMenus = styled.div`
  background: ${colors.navGray};
  padding-top: 8.5px;
`;

const UserBtnGrp = styled.div`
  padding: 24px 26px;
  position: relative;
  background: white;
  &:before {
    content: "";
    width: 100%;
    height: 4px;
    background: var(--primary);
    position: absolute;
    left: 0;
    z-index: 2;
    top: 0px;
  }
  .lgn-btn {
    width: 100%;
    margin-bottom: 1px;
    padding-left: 62px;
    padding-right: 0;
    background: var(--nav-gray);
    justify-content: start;
    color: black;
    border: none;
    &:hover {
      background: var(--primary);
      color: var(--white);
      text-decoration: underline;
      p {
        font-weight: 700;
      }
    }
  }
`;

const UserMenuContent = () => {
  const { authenticated, profileName } = useSelector(({ auth, user }) => ({
    authenticated: auth.isLoggedIn,
    profileName:
      user &&
      user.profile &&
      (user.profile.first_name || user.profile.login_name),
  }));

  const dispatch = useDispatch();
  const containerRef = useRef();

  useAutoIframeHeight(containerRef, "userMenuHeight");

  useEffect(() => {
    if (!isIframed) {
      document.body.classList.add("auth-open");
      return () => {
        document.body.classList.remove("auth-open");
      };
    }
  }, []);

  useEffect(() => {
    if (profileName && isIframed) {
      window.parent.postMessage(
        { userData: { name: profileName } },
        MARKETINGSITE
      );
    }
  }, [profileName]);

  const handleSignout = () => {
    dispatch(authActions.logout());
  };

  const handleSignup = (e) => {
    if (isIframed) {
      e.preventDefault();
      window.parent.postMessage({ redirect: "/create-account" }, MARKETINGSITE);
    }
  };

  return authenticated ? (
    <UserMenus ref={containerRef}>
      <UserBtnGrp className="mx-auto">
        <LinkToApp
          link="/ship-with-account"
          linkClasses="lgn-btn btn btn-primary"
        >
          <p className="mb-0">Create a shipping label</p>
        </LinkToApp>
        <LinkToCommon
          marketingLink="/schedule-pickup"
          appLink="/account-schedule-pickup"
          linkClasses="lgn-btn btn btn-primary"
        >
          <p className="mb-0">Schedule pickup</p>
        </LinkToCommon>
        <LinkToMarketing
          link="/calculate-rates"
          linkClasses="lgn-btn btn btn-primary"
        >
          <p className="mb-0">Calculate rates/quote</p>
        </LinkToMarketing>
        <LinkToMarketing link="/pricing" linkClasses="lgn-btn btn btn-primary">
          <p className="mb-0">Delivery options</p>
        </LinkToMarketing>
        <LinkToMarketing
          link="/cookie-program"
          linkClasses="lgn-btn btn btn-primary"
        >
          <p className="mb-0">Customer Cookie Program</p>
        </LinkToMarketing>
      </UserBtnGrp>
      <LinkToApp link="/my-account" linkClasses="lgn-btn w-100 btn btn-primary">
        <p className="text-meta mb-0">Manage your account</p>
      </LinkToApp>
      <Button
        block
        variant="link"
        className="font-weight-bold bg-white"
        onClick={handleSignout}
      >
        <u>Sign out</u>
      </Button>
    </UserMenus>
  ) : (
    <Tab.Container id="user-menu-tabs" defaultActiveKey="first">
      <TabStyled ref={containerRef}>
        <Row className="header-wrapper pt-2">
          <Col className="px-0 mr-2">
            <Nav variant="pills" className="flex-column">
              <Nav.Item>
                <Nav.Link
                  eventKey="first"
                  className="tab-btn text-big bolder text-center"
                >
                  Login
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
          <Col className="px-0 ml-2 my-auto">
            <Nav variant="pills" className="flex-column">
              <a
                onClick={handleSignup}
                href={`${MARKETINGSITE}/create-account`}
                target="_blank"
                rel="noopener noreferrer"
                className="sign-up m-auto tab-btn text-big text-center"
              >
                Sign up{" "}
                <span className="fe fe-arrow-right font-weight-bold align-middle small ml-8px"></span>
              </a>
            </Nav>
          </Col>
        </Row>
        <Tab.Content>
          <Tab.Pane eventKey="first">
            <Login />
          </Tab.Pane>
        </Tab.Content>
      </TabStyled>
    </Tab.Container>
  );
};

export default UserMenuContent;
